import type { UseQueryResult } from "@tanstack/react-query";
import { ArrowDown, ArrowUp } from "lucide-react";
import React from "react";

import {
  bandTone,
  BUCKET_EXPIRED,
  bucketTone,
  EXPIRY_RISK_PCTS,
  type ExpiryBand,
  type ExpiryBandTotal,
  type ExpiryRisk,
  type ExpiryRiskPct,
  type ExpiryRiskRow,
} from "@/api/warehouse_dashboard";
import { fmtDate, fmtPercent, ruNum } from "@/lib/format";
import { LoadState } from "@/lib/scan-ui/screen";
import { cn } from "@/lib/utils";
import {
  ALL,
  daysLeftText,
  DEFAULT_RISK_SORT,
  EMPTY_RISK_FILTER,
  filterRiskRows,
  hiddenRiskLine,
  nextRiskSort,
  type RiskFilter,
  type RiskSort,
  type RiskSortKey,
  riskWarehouseKey,
  riskWarehouseOptions,
  sortRiskRows,
} from "@/lib/warehouse-expiry-risk";
import { fmtRubExact, fmtRubShort } from "@/lib/warehouse-overview";

import { DashboardWarnings } from "./DashboardWarnings";

/** Сколько строк списка показать до «Показать все». */
export const RISK_ROWS_LIMIT = 100;

const CONTROL =
  "h-7 rounded border border-slate-300 bg-white px-2 text-[13px] outline-none focus:border-slate-500";

/**
 * «Просрочка и риск» — верх вкладки «Сроки годности»: партии с истёкшим сроком
 * и с ОСГ ниже порога по ВСЕМ складам всех юрлиц одним списком (жалоба
 * владельца: «приходится раскрывать всё и искать»). Порог — переключатель,
 * он же в ключе запроса. Плитки по полосам, сводка по складам, список с
 * фильтрами и сортировкой. Ответ рисуется как есть: полосы, проценты и дни —
 * бэкенда; `warnings` — плашкой; скрытые юрлица — строкой.
 */
export function ExpiryRiskBlock({
  q,
  pct,
  onPctChange,
}: {
  q: UseQueryResult<ExpiryRisk>;
  pct: ExpiryRiskPct;
  onPctChange: (pct: ExpiryRiskPct) => void;
}): React.ReactElement {
  return (
    <section aria-label="Просрочка и риск" className="space-y-2 rounded-lg border bg-white p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="min-w-0">
          <h2 className="text-sm font-semibold">Просрочка и риск</h2>
          <p className="text-xs text-muted-foreground">
            Все склады всех юрлиц: просрочено и остаток срока меньше {pct} %
          </p>
        </div>
        <div role="radiogroup" aria-label="Порог ОСГ" className="flex rounded-md border p-0.5">
          {EXPIRY_RISK_PCTS.map((p) => (
            <button
              key={p}
              type="button"
              role="radio"
              aria-checked={p === pct}
              onClick={() => onPctChange(p)}
              className={cn(
                "rounded px-2.5 py-0.5 text-[13px] tabular-nums",
                p === pct ? "bg-slate-800 text-white" : "text-slate-600 hover:bg-slate-100",
              )}
            >
              &lt; {p} %
            </button>
          ))}
        </div>
      </div>
      {q.isLoading || q.error || !q.data ? (
        <LoadState
          isLoading={q.isLoading}
          error={q.error}
          onRetry={() => void q.refetch()}
          className="flex items-center gap-3 py-2 text-left text-[13px] [&_svg]:size-4"
        />
      ) : (
        <RiskBody data={q.data} />
      )}
    </section>
  );
}

function RiskBody({ data }: { data: ExpiryRisk }): React.ReactElement {
  const [filter, setFilter] = React.useState<RiskFilter>(EMPTY_RISK_FILTER);
  const hidden = hiddenRiskLine(data.hidden);
  // Выбранный склад пропал из нового ответа (другой порог) — фильтр по нему
  // показал бы пустой список без причины; сбрасываем на «все».
  const options = riskWarehouseOptions(data.by_warehouse);
  const effective =
    filter.warehouse !== ALL && !options.some((o) => o.key === filter.warehouse)
      ? { ...filter, warehouse: ALL }
      : filter;
  return (
    <div className="space-y-3">
      <DashboardWarnings warnings={data.warnings} />
      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
        {data.totals.map((t) => (
          <BandTile
            key={t.band}
            total={t}
            active={effective.band === t.band}
            onClick={() =>
              setFilter((f) => ({ ...f, band: f.band === t.band ? ALL : t.band }))
            }
          />
        ))}
      </div>
      {data.by_warehouse.length > 0 && (
        <WarehouseSummary
          data={data}
          selected={effective.warehouse}
          onSelect={(key) =>
            setFilter((f) => ({ ...f, warehouse: f.warehouse === key ? ALL : key }))
          }
        />
      )}
      {hidden && <p className="text-xs text-muted-foreground">{hidden}</p>}
      <RiskTable
        data={data}
        filter={effective}
        options={options}
        onFilter={setFilter}
      />
    </div>
  );
}

function BandTile({
  total: t,
  active,
  onClick,
}: {
  total: ExpiryBandTotal;
  active: boolean;
  onClick: () => void;
}): React.ReactElement {
  const tone = bandTone(t.band);
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      title={`${t.title}: ${fmtRubExact(t.cost_rub)} · ${ruNum.format(t.qty)} шт · показать только эту полосу`}
      className={cn(
        "rounded-md border px-3 py-2 text-left transition-shadow hover:shadow-sm",
        tone.tile,
        active && "ring-2 ring-slate-400",
      )}
    >
      <span className="flex items-center gap-1.5 text-xs font-medium text-slate-600">
        <span className={cn("size-2 rounded-full", tone.dot)} />
        {t.title}
      </span>
      <span className={cn("mt-0.5 block text-xl font-semibold tabular-nums", tone.text)}>
        {fmtRubShort(t.cost_rub)}
      </span>
      <span className="block text-xs tabular-nums text-muted-foreground">
        {ruNum.format(t.qty)} шт · {ruNum.format(t.positions)} поз.
      </span>
    </button>
  );
}

/** Юрлицо · склад → ₽ по полосам; клик — фильтр списка по складу. */
function WarehouseSummary({
  data,
  selected,
  onSelect,
}: {
  data: ExpiryRisk;
  selected: string;
  onSelect: (key: string) => void;
}): React.ReactElement {
  const bands = data.totals;
  return (
    <div className="overflow-x-auto rounded-md border">
      <table className="w-full min-w-[640px] table-fixed text-[13px] leading-5">
        <colgroup>
          <col />
          {bands.map((b) => (
            <col key={b.band} className="w-[120px]" />
          ))}
          <col className="w-[128px]" />
        </colgroup>
        <thead className="border-b bg-slate-50 text-xs text-muted-foreground">
          <tr>
            <th className="px-2 py-1 pl-3 text-left font-medium">Юрлицо · склад</th>
            {bands.map((b) => (
              <th key={b.band} className="px-2 py-1 text-right font-medium">
                <span className="inline-flex items-center gap-1">
                  <span className={cn("size-1.5 rounded-full", bandTone(b.band).dot)} />
                  {b.title}
                </span>
              </th>
            ))}
            <th className="px-2 py-1 pr-3 text-right font-medium">Итого</th>
          </tr>
        </thead>
        <tbody>
          {data.by_warehouse.map((w) => {
            const key = riskWarehouseKey(w.organization_ref, w.warehouse_ref);
            const label = `${w.organization_name} · ${w.warehouse_name}`;
            return (
              <tr
                key={key}
                onClick={() => onSelect(key)}
                aria-selected={selected === key}
                className={cn(
                  "cursor-pointer border-b last:border-b-0 hover:bg-sky-50/60",
                  selected === key && "bg-sky-50",
                )}
              >
                <td className="truncate px-2 py-1 pl-3" title={label}>
                  <span className="text-muted-foreground">{w.organization_name} · </span>
                  {w.warehouse_name}
                </td>
                {bands.map((b) => {
                  const cell = w.bands[b.band];
                  return (
                    <td
                      key={b.band}
                      className={cn(
                        "whitespace-nowrap px-2 py-1 text-right tabular-nums",
                        cell ? bandTone(b.band).text : "text-muted-foreground/60",
                      )}
                      title={cell ? `${ruNum.format(cell.qty)} шт · ${cell.positions} поз.` : undefined}
                    >
                      {cell ? fmtRubExact(cell.cost_rub) : "—"}
                    </td>
                  );
                })}
                <td className="whitespace-nowrap px-2 py-1 pr-3 text-right font-medium tabular-nums">
                  {fmtRubExact(w.cost_rub)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function SortHeader({
  label,
  sortKey,
  sort,
  onSort,
}: {
  label: string;
  sortKey: RiskSortKey;
  sort: RiskSort;
  onSort: (key: RiskSortKey) => void;
}): React.ReactElement {
  const active = sort.key === sortKey;
  const Icon = sort.dir === "asc" ? ArrowUp : ArrowDown;
  return (
    <th
      className="px-2 py-1.5 text-right font-medium"
      aria-sort={active ? (sort.dir === "asc" ? "ascending" : "descending") : "none"}
    >
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        className={cn(
          "inline-flex items-center gap-0.5 hover:text-foreground",
          active && "text-foreground",
        )}
      >
        {label}
        {active && <Icon className="size-3" />}
      </button>
    </th>
  );
}

function RiskTable({
  data,
  filter,
  options,
  onFilter,
}: {
  data: ExpiryRisk;
  filter: RiskFilter;
  options: { key: string; label: string }[];
  onFilter: React.Dispatch<React.SetStateAction<RiskFilter>>;
}): React.ReactElement {
  const [sort, setSort] = React.useState<RiskSort>(DEFAULT_RISK_SORT);
  const [showAll, setShowAll] = React.useState(false);
  const found = sortRiskRows(filterRiskRows(data.rows, filter), sort);
  const shown = showAll ? found : found.slice(0, RISK_ROWS_LIMIT);
  const filtered =
    filter.band !== ALL || filter.warehouse !== ALL || filter.search.trim() !== "";

  return (
    <div className="space-y-1.5">
      <div className="flex flex-wrap items-center gap-2">
        <select
          aria-label="Полоса"
          value={filter.band}
          onChange={(e) =>
            onFilter((f) => ({ ...f, band: e.target.value as ExpiryBand | typeof ALL }))
          }
          className={CONTROL}
        >
          <option value={ALL}>Все полосы</option>
          {data.totals.map((t) => (
            <option key={t.band} value={t.band}>
              {t.title}
            </option>
          ))}
        </select>
        <select
          aria-label="Юрлицо и склад"
          value={filter.warehouse}
          onChange={(e) => onFilter((f) => ({ ...f, warehouse: e.target.value }))}
          className={cn(CONTROL, "max-w-[320px]")}
        >
          <option value={ALL}>Все склады</option>
          {options.map((o) => (
            <option key={o.key} value={o.key}>
              {o.label}
            </option>
          ))}
        </select>
        <input
          type="search"
          value={filter.search}
          onChange={(e) => onFilter((f) => ({ ...f, search: e.target.value }))}
          placeholder="Артикул или название"
          aria-label="Поиск по списку риска"
          className={cn(CONTROL, "w-60 max-w-full")}
        />
        <span className="text-xs tabular-nums text-muted-foreground">
          {filtered
            ? `найдено ${ruNum.format(found.length)} из ${ruNum.format(data.rows.length)}`
            : `${ruNum.format(data.rows.length)} партий`}
        </span>
        {filtered && (
          <button
            type="button"
            onClick={() => onFilter(EMPTY_RISK_FILTER)}
            className="text-xs font-medium text-sky-700 hover:underline"
          >
            Сбросить
          </button>
        )}
      </div>
      <div className="overflow-x-auto rounded-md border">
        <table className="w-full min-w-[1080px] table-fixed text-[13px] leading-5">
          <colgroup>
            <col className="w-[220px]" />
            <col />
            <col className="w-[104px]" />
            <col className="w-[84px]" />
            <col className="w-[92px]" />
            <col className="w-[124px]" />
            <col className="w-[92px]" />
            <col className="w-[88px]" />
            <col className="w-[112px]" />
          </colgroup>
          <thead className="border-b bg-slate-50 text-xs text-muted-foreground">
            <tr className="text-left">
              <th className="px-2 py-1.5 pl-3 font-medium">Склад · юрлицо</th>
              <th className="px-2 py-1.5 font-medium">Артикул · название</th>
              <th className="px-2 py-1.5 font-medium">Партия</th>
              <th className="px-2 py-1.5 font-medium">Произв.</th>
              <SortHeader label="Годен до" sortKey="expires" sort={sort} onSort={(k) => setSort((s) => nextRiskSort(s, k))} />
              <th className="px-2 py-1.5 font-medium">Срок</th>
              <th className="px-2 py-1.5 font-medium">ОСГ</th>
              <SortHeader label="Кол-во" sortKey="qty" sort={sort} onSort={(k) => setSort((s) => nextRiskSort(s, k))} />
              <SortHeader label="₽" sortKey="cost" sort={sort} onSort={(k) => setSort((s) => nextRiskSort(s, k))} />
            </tr>
          </thead>
          <tbody>
            {shown.map((r) => (
              <RiskRow key={rowKey(r)} row={r} />
            ))}
            {found.length === 0 && (
              <tr>
                <td colSpan={9} className="px-3 py-2 text-muted-foreground">
                  {data.rows.length === 0
                    ? "Просрочки и партий ниже порога нет"
                    : "Ничего не найдено по фильтрам"}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {!showAll && found.length > RISK_ROWS_LIMIT && (
        <button
          type="button"
          onClick={() => setShowAll(true)}
          className="text-xs font-medium text-sky-700 hover:underline"
        >
          Показать все {ruNum.format(found.length)} (показано {ruNum.format(RISK_ROWS_LIMIT)})
        </button>
      )}
    </div>
  );
}

function rowKey(r: ExpiryRiskRow): string {
  return [r.organization_ref, r.warehouse_ref, r.nomenclature_ref, r.characteristic_ref, r.batch_ref].join("|");
}

function RiskRow({ row: r }: { row: ExpiryRiskRow }): React.ReactElement {
  const place = `${r.organization_name} · ${r.warehouse_name}`;
  const item = [r.article, r.name, r.characteristic].filter(Boolean).join(" · ");
  const expired = r.days_left < 0;
  const tone = bucketTone(r.bucket);
  return (
    <tr className="border-b border-slate-100 last:border-b-0 hover:bg-sky-50/60">
      {/* Склад — первым: в узкой колонке обрезается юрлицо, не склад. */}
      <td className="truncate px-2 py-1 pl-3" title={place}>
        {r.warehouse_name}
        <span className="text-muted-foreground"> · {r.organization_name}</span>
      </td>
      <td className="px-2 py-1" title={item}>
        <span className="flex min-w-0 items-center gap-1.5">
          {r.article && (
            <span className="shrink-0 tabular-nums text-muted-foreground">{r.article} ·</span>
          )}
          <span className="min-w-0 truncate">{r.name}</span>
          {r.characteristic && (
            <span
              className="max-w-[40%] shrink-0 truncate whitespace-nowrap rounded border border-slate-200 bg-white px-1 text-[11px] leading-4 text-slate-600"
              title={`Характеристика: ${r.characteristic}`}
            >
              {r.characteristic}
            </span>
          )}
        </span>
      </td>
      <td className="truncate px-2 py-1 tabular-nums" title={r.batch ? `№ ${r.batch}` : undefined}>
        {r.batch ? `№ ${r.batch}` : "—"}
      </td>
      <td className="whitespace-nowrap px-2 py-1 tabular-nums text-muted-foreground">
        {r.produced ? fmtDate(r.produced) : "—"}
      </td>
      <td className="whitespace-nowrap px-2 py-1 text-right tabular-nums">{fmtDate(r.expires)}</td>
      <td
        className={cn(
          "whitespace-nowrap px-2 py-1 text-xs",
          expired ? "font-medium text-rose-700" : "text-muted-foreground",
        )}
      >
        {daysLeftText(r.days_left)}
      </td>
      <td className="px-2 py-1">
        <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-1.5 text-[11px] leading-4 tabular-nums text-slate-700">
          <span className={cn("size-1.5 rounded-full", tone.dot)} />
          {r.shelf_life_pct !== null
            ? fmtPercent(r.shelf_life_pct)
            : r.bucket === BUCKET_EXPIRED
              ? "просрочено"
              : r.bucket}
        </span>
      </td>
      <td className="whitespace-nowrap px-2 py-1 text-right tabular-nums">{ruNum.format(r.qty)}</td>
      <td className="whitespace-nowrap px-2 py-1 pr-3 text-right font-medium tabular-nums">
        {r.cost_rub === null ? "—" : fmtRubExact(r.cost_rub)}
      </td>
    </tr>
  );
}
